export type Category =
  | "text"
  | "image"
  | "developer"
  | "pdf"
  | "converter"
  | "generator";

export interface Tool {
  slug: string;
  name: string;
  category: Category;
  description: string;
  icon: string;
  keywords: string[];
}

export const CATEGORIES: Record<
  Category,
  { label: string; description: string; icon: string }
> = {
  text: {
    label: "Text Tools",
    description:
      "Count, transform, clean and format text in seconds — no signup required.",
    icon: "text",
  },
  image: {
    label: "Image Tools",
    description:
      "Resize, compress and convert images right in your browser for free.",
    icon: "image",
  },
  developer: {
    label: "Developer Tools",
    description:
      "Everyday utilities for developers: JSON, encoding, hashing and more.",
    icon: "code",
  },
  pdf: {
    label: "PDF Tools",
    description:
      "Merge, split and compress PDF files securely without uploading.",
    icon: "pdf",
  },
  converter: {
    label: "Converters",
    description:
      "Fast unit and color converters for everyday calculations.",
    icon: "convert",
  },
  generator: {
    label: "Generators",
    description:
      "Generate passwords, QR codes, UUIDs and random content instantly.",
    icon: "generate",
  },
};

export const CATEGORY_ORDER: Category[] = [
  "text",
  "image",
  "developer",
  "pdf",
  "converter",
  "generator",
];

export const tools: Tool[] = [
  {
    slug: "word-counter",
    name: "Word Counter",
    category: "text",
    description:
      "Count words, characters, sentences, and paragraphs, and estimate reading time.",
    icon: "text",
    keywords: [
      "word counter",
      "character counter",
      "sentence counter",
      "paragraph counter",
      "reading time calculator",
      "text statistics",
    ],
  },
  {
    slug: "case-converter",
    name: "Case Converter",
    category: "text",
    description:
      "Convert text to uppercase, lowercase, title case and more.",
    icon: "text",
    keywords: [
      "case converter",
      "uppercase",
      "lowercase",
      "title case",
      "sentence case",
      "text case",
    ],
  },
  {
    slug: "image-cropper",
    name: "Image Cropper & Rotator",
    category: "image",
    description:
      "Crop, rotate, flip and download images directly in your browser.",
    icon: "image",
    keywords: [
      "image cropper",
      "rotate image",
      "flip image",
      "crop photo",
    ],
  },
  {
    slug: "image-compressor",
    name: "Image Compressor",
    category: "image",
    description:
      "Compress images to reduce file size without losing visible quality.",
    icon: "image",
    keywords: [
      "compress image",
      "image compressor",
      "reduce image size",
      "shrink jpg",
    ],
  },
  {
    slug: "base64-encoder",
    name: "Base64 Encoder",
    category: "developer",
    description:
      "Encode and decode text or files to and from Base64 instantly.",
    icon: "code",
    keywords: ["base64 encode", "base64 decode", "base64 converter"],
  },
  {
    slug: "markdown-to-html",
    name: "Markdown to HTML Converter",
    category: "developer",
    description:
      "Convert Markdown into live HTML with a split-pane editor and preview.",
    icon: "code",
    keywords: [
      "markdown to html",
      "markdown converter",
      "md to html",
      "markdown preview",
    ],
  },
  {
    slug: "pdf-merge-split",
    name: "PDF Merge & Split Tool",
    category: "pdf",
    description:
      "Merge multiple PDFs into one or split a PDF into smaller files in your browser.",
    icon: "pdf",
    keywords: [
      "merge pdf",
      "split pdf",
      "combine pdf",
      "extract pdf pages",
      "pdf merger",
    ],
  },
  {
    slug: "hsb-colour-picker",
    name: "HSB Color Picker",
    category: "converter",
    description:
      "Pick a color and convert it between HEX, RGB, HSL and HSB instantly.",
    icon: "convert",
    keywords: [
      "hsb color picker",
      "hsb colour picker",
      "color picker",
      "hex to rgb",
      "hsl converter",
      "color converter",
    ],
  },
  {
    slug: "unit-converter",
    name: "Unit Converter",
    category: "converter",
    description:
      "Convert between common units for length, weight, temperature, volume, area and speed.",
    icon: "convert",
    keywords: [
      "unit converter",
      "length weight temperature converter",
      "speed converter",
      "volume converter",
    ],
  },
  {
    slug: "password-generator",
    name: "Password Generator",
    category: "generator",
    description:
      "Create strong, secure random passwords with customizable options.",
    icon: "generate",
    keywords: [
      "password generator",
      "strong password",
      "random password",
      "secure password",
    ],
  },
  {
    slug: "qr-code-generator",
    name: "QR Code Generator",
    category: "generator",
    description:
      "Generate QR codes for URLs, text, WiFi and more — free and private.",
    icon: "generate",
    keywords: ["qr code generator", "create qr code", "qr generator free"],
  },
    {
    slug: "fancy-text-generator",
    name: "Fancy Text Generator",
    category: "text",
    description:
      "Turn plain text into bold, script, small caps and other Unicode styles for Instagram, TikTok and Discord.",
    icon: "sparkle",
    keywords: [
      "fancy text generator",
      "instagram fonts",
      "instagram font generator",
      "bold text generator",
      "cursive text generator",
      "small caps generator",
      "cool fonts for bio",
      "stylish text",
    ],
  },
];

export const TOOL_COUNT = tools.length;

/** Canonical public URL for a tool. Tools intentionally live at the site root. */
export function getToolPath(slug: string): string {
  return `/${slug}`;
}

export function getTool(slug: string): Tool | undefined {
  return tools.find(
    (tool) =>
      tool.slug === slug ||
      (slug === "color-picker" && tool.slug === "hsb-colour-picker") ||
      (slug === "hsb-color-picker" && tool.slug === "hsb-colour-picker")
  );
}

export function getToolsByCategory(category: Category): Tool[] {
  return tools.filter((tool) => tool.category === category);
}

export function getRelatedTools(tool: Tool, count = 4): Tool[] {
  const sameCategory = tools.filter(
    (t) => t.category === tool.category && t.slug !== tool.slug
  );
  const others = tools.filter((t) => t.category !== tool.category);
  return [...sameCategory, ...others].slice(0, count);
}